import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type * as GeoJSON from "geojson";
import {
  Badge,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  IconButton,
  InputBase,
  Paper,
  Popover,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import SettingsIcon from "@mui/icons-material/Settings";
import TuneIcon from "@mui/icons-material/Tune";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PublishIcon from "@mui/icons-material/Publish";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import TitleIcon from "@mui/icons-material/Title";
import NumbersIcon from "@mui/icons-material/Numbers";
import TableChartIcon from "@mui/icons-material/TableChart";
import ImageIcon from "@mui/icons-material/Image";
import LayersIcon from "@mui/icons-material/Layers";
import CompareIcon from "@mui/icons-material/Compare";
import MovieIcon from "@mui/icons-material/Movie";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";

import { useSimulationResultsFor } from "../api/simulations";
import { useStoryMap, useUpdateStoryMap } from "../api/storyMaps";
import { useUiFlag } from "../hooks/useUiFlag";
import {
  SceneCompare,
  SceneLayerEntry,
  SceneMode,
  StoryBlock,
  StoryBlockType,
  StoryContent,
  StoryScene,
  StorySceneAnimation,
  StoryTransition,
  LegendPosition,
  SceneLegend,
} from "../types/storyMap";
import StoryMapCanvas, {
  StoryMapCanvasHandle,
} from "../components/storyMap/StoryMapCanvas";
import {
  IGNITION_CIRCLE_PAINT,
  INSPECTION_POINT_PAINT,
  StoryCanvasLayer,
} from "../components/storyMap/storyLayers";
import { entryProjectId, sceneLayerEntries } from "../components/storyMap/layerEntries";
import { storyMapPaths } from "../components/storyMap/storyPaths";
import { scopeByline, useStoryScope } from "../components/storyMap/useStoryScope";
import { useScopeLists } from "../components/storyMap/useScopeLists";
import SceneLayerList from "../components/storyMap/SceneLayerList";
import StoryLegend from "../components/storyMap/StoryLegendCard";
import FreehandDrawTool from "../components/storyMap/FreehandDrawTool";
import WindParticleLayer from "../components/layer_components/WindParticleLayer";
import StoryFuel3DLayers from "../components/storyMap/StoryFuel3DLayers";
import WeatherPanelCard from "../components/storyMap/WeatherPanelCard";
import {
  assetsTilesByProject,
  bboxOfBoundary,
  useSceneLayerStack,
  useStorySceneSources,
} from "../components/storyMap/useStorySceneLayers";
import { useUnitSystem } from "../hooks/useUnitSystem";
import type { Map as MapboxMap } from "mapbox-gl";
import ShareDialog from "../components/storyMap/ShareDialog";
import SceneComments from "../components/storyMap/SceneComments";
import { openThreadCount } from "../components/storyMap/commentThreads";
import {
  useStoryMapCommentActions,
  useStoryMapComments,
} from "../api/storyMapComments";
import { useAuth } from "../contexts/AuthContext";
import {
  defaultSceneAnimation,
  defaultSceneCompare,
  defaultStoryContent,
  newBlock,
  newScene,
} from "../components/storyMap/storyDefaults";
import CompareMapView, {
  CompareMapViewHandle,
} from "../components/storyMap/CompareMapView";
import CompareSceneSettings from "../components/storyMap/CompareSceneSettings";
import AnimationSceneSettings from "../components/storyMap/AnimationSceneSettings";
import { useSceneAnimations } from "../components/storyMap/useSceneAnimations";
import {
  extractIgnitionPoints,
  ignitionPointsToGeoJson,
} from "../components/storyMap/ignitionPoints";
import HelpTip, { HelpLabel } from "../components/storyMap/HelpTip";
import { TOUR_ANCHOR } from "../components/storyMap/storyTour";
import { useStoryBuilderTour } from "../components/storyMap/useStoryBuilderTour";
import BlockShell from "../components/storyMap/blocks/BlockShell";
import TextBlockEditor from "../components/storyMap/blocks/TextBlockEditor";
import MetricBlockEditor from "../components/storyMap/blocks/MetricBlockEditor";
import MetricsRowBlockEditor from "../components/storyMap/blocks/MetricsRowBlockEditor";
import ImageBlockEditor from "../components/storyMap/blocks/ImageBlockEditor";

const TEAL = "#00d4ad";
const SLATE = "#212b37";
const TOPBAR_HEIGHT = "var(--topbar-h)";
const HEADER_PX = 56;

const BLOCK_PALETTE: { type: StoryBlockType; label: string; icon: React.ReactNode }[] = [
  { type: "text", label: "Text", icon: <TextFieldsIcon fontSize="small" /> },
  { type: "heading", label: "Heading", icon: <TitleIcon fontSize="small" /> },
  { type: "metric", label: "Metric", icon: <NumbersIcon fontSize="small" /> },
  { type: "metrics_row", label: "Metrics row", icon: <TableChartIcon fontSize="small" /> },
  { type: "image", label: "Image", icon: <ImageIcon fontSize="small" /> },
];

const BLOCK_LABELS: Record<StoryBlockType, string> = {
  text: "Text",
  heading: "Heading",
  metric: "Metric",
  metrics_row: "Metrics row",
  image: "Image",
};

const IGNITION_MARKERS_LAYER_ID = "story-ignition-markers";
const INSPECTION_POINT_LAYER_ID = "story-inspection-point";

function moveItem<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

export default function StoryMapBuilderPage() {
  const navigate = useNavigate();
  // The story is addressed by id alone; its scope (one project, a folder, the
  // whole organisation) comes from the row, not the URL.
  const { storyMapId } = useParams<{ storyMapId: string }>();

  const { data: storyData } = useStoryMap(storyMapId);
  const story = storyData?.data ?? null;
  const scope = useStoryScope(story);
  const scopeLabel = scopeByline(scope.kind, scope.name);
  const updateStory = useUpdateStoryMap();
  const { user } = useAuth();
  const browseFlag = useUiFlag("projectListingBrowse");

  // Reader comments land on the published story; the builder shows each
  // scene's threads beside its narrative so the author can answer them.
  const commentsQuery = useStoryMapComments(storyMapId);
  const commentActions = useStoryMapCommentActions(storyMapId);
  const comments = useMemo(() => commentsQuery.data?.data ?? [], [commentsQuery.data]);
  const commentsBusy =
    commentActions.create.isPending ||
    commentActions.resolve.isPending ||
    commentActions.remove.isPending;
  const commentsError =
    commentActions.create.error?.message ||
    commentActions.resolve.error?.message ||
    commentActions.remove.error?.message ||
    null;
  const openCommentsTotal = useMemo(
    () => comments.filter((c) => !c.parent_id && !c.resolved).length,
    [comments]
  );
  // The comments panel drops down from the top bar over the map, anchored to
  // its button; it follows the selected scene while open.
  const [commentsAnchor, setCommentsAnchor] = useState<HTMLElement | null>(null);

  // ----- local story state (source of truth while editing) -----
  const [title, setTitle] = useState("");
  const [content, setContent] = useState<StoryContent | null>(null);
  // Strategies, simulations and snapshots per project the story cites (or
  // the author picks in a dialog).
  const lists = useScopeLists(scope, content);
  const [sceneIdx, setSceneIdx] = useState(0);
  const [dirty, setDirty] = useState(false);
  // Authored edits specifically — derived writes (resolved tile URLs) set
  // `dirty` so they persist, but must not prompt on tab close.
  const [userDirty, setUserDirty] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [, setTick] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);
  const [storySettingsOpen, setStorySettingsOpen] = useState(false);
  const loadedRef = useRef(false);
  const canvasRef = useRef<StoryMapCanvasHandle | null>(null);

  useEffect(() => {
    if (storyData?.data && !loadedRef.current) {
      loadedRef.current = true;
      setTitle(storyData.data.title);
      setContent(storyData.data.content ?? defaultStoryContent());
    }
  }, [storyData]);

  // Re-render every 30s so "Saved N min ago" stays current.
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  // ----- autosave: debounce ~1.5s after any change -----
  // `dirty` is cleared in onSuccess, never before the request: a 4xx, 5xx or
  // network blip would otherwise drop that batch of edits with no retry and
  // nothing on screen to say so.
  const save = () => {
    if (!content || !storyMapId) return;
    setSaveError(null);
    updateStory.mutate(
      { storyMapId, data: { title, content } },
      {
        onSuccess: () => {
          setDirty(false);
          setUserDirty(false);
          setSavedAt(new Date());
        },
        onError: () => {
          // Leave `dirty` set so the next edit — or the retry button —
          // attempts the same content again.
          setSaveError("Couldn't save your changes. Retrying on your next edit.");
        },
      }
    );
  };

  useEffect(() => {
    if (!dirty || !content || !storyMapId || updateStory.isPending) return;
    const timer = setTimeout(save, 1500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, title, content, storyMapId, updateStory.isPending]);

  const flushSave = () => {
    if (dirty && content && storyMapId) save();
  };

  // Closing the tab or hitting browser-back inside the debounce window would
  // otherwise lose the last edits silently. (The in-app back button flushes.)
  useEffect(() => {
    if (!userDirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [userDirty]);

  // Unmounting (route change) is the same loss, without the browser prompt:
  // fire the pending save on the way out. Reads refs so the cleanup doesn't
  // re-run on every keystroke.
  const pendingSaveRef = useRef<{ dirty: boolean; save: () => void }>({ dirty, save });
  pendingSaveRef.current = { dirty, save };
  useEffect(
    () => () => {
      if (pendingSaveRef.current.dirty) pendingSaveRef.current.save();
    },
    []
  );

  const saveLabel = (() => {
    if (saveError) return "Not saved";
    if (updateStory.isPending) return "Saving…";
    if (dirty) return "Saving…";
    if (savedAt) {
      const mins = Math.floor((Date.now() - savedAt.getTime()) / 60000);
      return mins < 1 ? "Saved just now" : `Saved ${mins} min ago`;
    }
    return "";
  })();

  // ----- mutators -----
  // `derived` marks a write the app made on the author's behalf (resolved tile
  // URLs, say) rather than an edit they typed. It still needs saving, but it
  // shouldn't be announced as unsaved work or prompt on tab close.
  const editContent = (
    updater: (c: StoryContent) => StoryContent,
    { derived = false }: { derived?: boolean } = {}
  ) => {
    setContent((c) => (c ? updater(c) : c));
    setDirty(true);
    if (!derived) setUserDirty(true);
  };

  const patchScene = (
    index: number,
    patch: Partial<StoryScene>,
    options?: { derived?: boolean }
  ) =>
    editContent(
      (c) => ({
        ...c,
        scenes: c.scenes.map((s, i) => (i === index ? { ...s, ...patch } : s)),
      }),
      options
    );

  const patchCompare = (patch: Partial<SceneCompare>) =>
    editContent((c) => ({
      ...c,
      scenes: c.scenes.map((s, i) =>
        i === sceneIdx
          ? { ...s, compare: { ...(s.compare ?? defaultSceneCompare()), ...patch } }
          : s
      ),
    }));

  // ----- animation entries (scene.animations, ordered bottom→top) -----
  const patchAnimations = (
    updater: (entries: StorySceneAnimation[]) => StorySceneAnimation[]
  ) =>
    editContent((c) => ({
      ...c,
      scenes: c.scenes.map((s, i) =>
        i === sceneIdx ? { ...s, animations: updater(s.animations ?? []) } : s
      ),
    }));

  const patchAnimationEntry = (
    entryId: string,
    patch: Partial<StorySceneAnimation>
  ) =>
    patchAnimations((entries) =>
      entries.map((entry) =>
        entry.id === entryId ? { ...entry, ...patch } : entry
      )
    );

  // Entry the map's ignition-marker clicks assign the partition to.
  const [selectedAnimId, setSelectedAnimId] = useState<string | null>(null);
  // Freehand entry whose drawing tool is open on the map (layers mode only).
  const [drawingEntryId, setDrawingEntryId] = useState<string | null>(null);
  // Map instance as state for the wind-particle DOM overlay.
  const [mapInstance, setMapInstance] = useState<MapboxMap | null>(null);
  const unitSystem = useUnitSystem();

  const addAnimationEntry = () => {
    const entry = defaultSceneAnimation();
    patchAnimations((entries) => [...entries, entry]);
    setSelectedAnimId(entry.id);
  };

  const removeAnimationEntry = (entryId: string) =>
    patchAnimations((entries) => entries.filter((e) => e.id !== entryId));

  const moveAnimationEntry = (entryId: string, delta: number) =>
    patchAnimations((entries) => {
      const from = entries.findIndex((e) => e.id === entryId);
      return from < 0 ? entries : moveItem(entries, from, from + delta);
    });

  const addScene = () => {
    editContent((c) => ({ ...c, scenes: [...c.scenes, newScene(c.scenes.length)] }));
    setSceneIdx(content ? content.scenes.length : 0);
  };

  const deleteScene = (index: number) => {
    editContent((c) =>
      c.scenes.length <= 1
        ? c
        : { ...c, scenes: c.scenes.filter((_, i) => i !== index) }
    );
    setSceneIdx((i) => Math.max(0, Math.min(i, (content?.scenes.length ?? 2) - 2)));
  };

  const moveScene = (index: number, delta: number) => {
    const target = index + delta;
    // moveItem is a no-op when the target is out of range; moving the
    // selection anyway drifts it off the scene it was following.
    if (target < 0 || target >= (content?.scenes.length ?? 0)) return;
    editContent((c) => ({ ...c, scenes: moveItem(c.scenes, index, target) }));
    setSceneIdx(target);
  };

  const patchBlock = (blockId: string, patch: Partial<StoryBlock>) =>
    patchSceneBlocks((blocks) =>
      blocks.map((b) => (b.id === blockId ? ({ ...b, ...patch } as StoryBlock) : b))
    );

  const patchSceneBlocks = (updater: (blocks: StoryBlock[]) => StoryBlock[]) =>
    editContent((c) => ({
      ...c,
      scenes: c.scenes.map((s, i) =>
        i === sceneIdx ? { ...s, blocks: updater(s.blocks) } : s
      ),
    }));

  const addBlock = (type: StoryBlockType) =>
    patchSceneBlocks((blocks) => [...blocks, newBlock(type)]);

  const duplicateBlock = (blockId: string) =>
    patchSceneBlocks((blocks) => {
      const index = blocks.findIndex((b) => b.id === blockId);
      if (index < 0) return blocks;
      const copy = { ...blocks[index], id: crypto.randomUUID() } as StoryBlock;
      const next = [...blocks];
      next.splice(index + 1, 0, copy);
      return next;
    });

  const scenes = content?.scenes ?? [];
  const scene = scenes[sceneIdx] as StoryScene | undefined;
  const sceneMode: SceneMode = scene?.mode ?? "layers";

  // First-run coach tour. Gated on a scene existing so it never highlights the
  // loading screen; replayable from the header's ? button.
  const tour = useStoryBuilderTour({ ready: !!content && !!scene });

  const setSceneMode = (value: SceneMode) => {
    if (!scene) return;
    const patch: Partial<StoryScene> = { mode: value };
    // Initialise the mode's config on first use so the rail has something to
    // edit. Animation mode starts with one entry.
    if (value === "compare" && !scene.compare) patch.compare = defaultSceneCompare();
    if (value === "animation" && !scene.animations?.length) {
      patch.animations = [defaultSceneAnimation()];
    }
    patchScene(sceneIdx, patch);
  };

  // ----- live builder map: the same sources + entry stack the viewer uses.
  // Layer entries carry their own configuration (strategy, date, regime,
  // simulation…) and per-entry tiles are persisted by the SceneLayerList
  // entry cards, so no scene-level resolution remains here. -----
  const sources = useStorySceneSources({ isPublic: false, content, scope });
  // What the map frames when a scene has no camera: the project's boundary,
  // or every cited project's extent.
  const boundaryFeature = sources.extentFeature;
  const sceneEntries = useMemo(() => sceneLayerEntries(scene), [scene]);
  const strategyNames = lists.strategyNames;

  // Leaving the scene or its layers mode closes the drawing tool.
  const drawingEntry =
    drawingEntryId && sceneMode === "layers"
      ? sceneEntries.find((e) => e.id === drawingEntryId && e.key === "freehand") ?? null
      : null;
  useEffect(() => {
    if (drawingEntryId && !drawingEntry) setDrawingEntryId(null);
  }, [drawingEntryId, drawingEntry]);
  useEffect(() => {
    setDrawingEntryId(null);
  }, [sceneIdx]);

  // ----- animation mode: sim results → ignition markers + frame playback -----
  const animations = useMemo<StorySceneAnimation[]>(
    () => (sceneMode === "animation" ? scene?.animations ?? [] : []),
    [sceneMode, scene?.animations]
  );
  // Ignition-marker clicks apply to the SELECTED entry (rail highlight).
  const selectedAnimEntry =
    animations.find((e) => e.id === selectedAnimId) ?? animations[0] ?? null;
  const selectedAnimEntryId = selectedAnimEntry?.id ?? null;
  const selectedAnimView = selectedAnimEntry?.view ?? "single";
  const selectedAnimPartition = selectedAnimEntry?.partition ?? null;
  const { data: animSimResults } = useSimulationResultsFor(
    selectedAnimEntry ? entryProjectId(selectedAnimEntry, scope.defaultProjectId) : null,
    selectedAnimEntry?.simulationId ?? null
  );
  const ignitionPoints = useMemo(
    () => extractIgnitionPoints(animSimResults?.data?.results),
    [animSimResults]
  );

  // Auto-select the partition when the simulation has exactly one ignition.
  useEffect(() => {
    if (!selectedAnimEntryId || selectedAnimView !== "single") return;
    if (selectedAnimPartition != null || ignitionPoints.length !== 1) return;
    patchAnimationEntry(selectedAnimEntryId, {
      partition: ignitionPoints[0].partition,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAnimEntryId, selectedAnimView, selectedAnimPartition, ignitionPoints]);

  const anim = useSceneAnimations({
    animations,
    defaultProjectId: scope.defaultProjectId,
    isPublic: false,
    enabled: sceneMode === "animation",
    avoidPosition: scene?.legend?.enabled ? scene.legend.position ?? "bottom-right" : null,
    assetsTilesUrl: sources.assetsTilesUrl,
    assetsTilesUrlByProject: assetsTilesByProject(sources),
    getMap: () => canvasRef.current?.getMap() ?? null,
  });

  const { layers: canvasLayers, legendRows, wind, weatherPanel, fuel3d } = useSceneLayerStack({
    scene,
    sources,
    isPublic: false,
    strategyNames,
    weatherElapsedSeconds: sceneMode === "animation" ? anim.elapsedSeconds : null,
    unitSystem,
  });
  const windBounds = useMemo(() => bboxOfBoundary(boundaryFeature), [boundaryFeature]);

  const selectedIgnition = useMemo(
    () =>
      selectedAnimPartition != null
        ? ignitionPoints.find(
            (p) => String(p.partition) === String(selectedAnimPartition)
          ) ?? null
        : null,
    [ignitionPoints, selectedAnimPartition]
  );

  // Live-map layer stack: scene layers, plus (animation mode) the per-entry
  // canvas animations and the clickable ignition markers (for the selected
  // entry) for partition picking. View "single" requires a pick; percentile/
  // confidence use it as an optional scope (hidden with a single ignition,
  // where scoping is meaningless).
  const inspectionEntry =
    sceneMode === "layers"
      ? sceneEntries.find((e) => e.key === "inspection") ?? null
      : null;
  const inspectionPoint = inspectionEntry?.inspectionPoint ?? null;

  const mapLayers = useMemo(() => {
    if (sceneMode !== "animation") {
      // While a freehand layer is being drawn, mapbox-gl-draw renders it;
      // drop the story's own copy so nothing draws twice.
      const visibleLayers = drawingEntryId
        ? canvasLayers.filter((l) => !l.id.startsWith(`story-${drawingEntryId}`))
        : canvasLayers;
      if (!inspectionPoint) return visibleLayers;
      // Mark the picked inspection point so the author can see what the
      // yellow cells are being tested against.
      return [
        ...visibleLayers,
        {
          id: INSPECTION_POINT_LAYER_ID,
          kind: "geojson-circle",
          data: {
            type: "Feature",
            properties: {},
            geometry: { type: "Point", coordinates: inspectionPoint },
          },
          paint: INSPECTION_POINT_PAINT,
          visible: true,
        } as StoryCanvasLayer,
      ];
    }
    const layers: StoryCanvasLayer[] = [...canvasLayers, ...anim.layers];
    if (
      selectedAnimView === "single"
        ? ignitionPoints.length > 0
        : ignitionPoints.length > 1
    ) {
      layers.push({
        id: IGNITION_MARKERS_LAYER_ID,
        kind: "geojson-circle",
        data: ignitionPointsToGeoJson(ignitionPoints, selectedAnimPartition),
        paint: IGNITION_CIRCLE_PAINT,
        visible: true,
        interactive: true,
      });
    }
    return layers;
  }, [
    canvasLayers,
    sceneMode,
    anim.layers,
    selectedAnimView,
    ignitionPoints,
    selectedAnimPartition,
    inspectionPoint,
    drawingEntryId,
  ]);

  const handleLayerClick = (layerId: string, feature: GeoJSON.Feature) => {
    if (layerId !== IGNITION_MARKERS_LAYER_ID || !selectedAnimEntryId) return;
    const partition = (feature.properties as { partition?: string | number })
      ?.partition;
    if (partition == null) return;
    // Ensemble views: the pick is an optional scope, so re-clicking the
    // selected marker clears it (back to every ignition). Single view always
    // needs a partition, so there clicks only ever switch.
    const clearing =
      selectedAnimView !== "single" &&
      selectedAnimPartition != null &&
      String(partition) === String(selectedAnimPartition);
    patchAnimationEntry(selectedAnimEntryId, {
      partition: clearing ? null : partition,
    });
  };

  // Inspection-point picking: with an Inspection layer added, clicking the
  // map sets the (first) inspection entry's hit-test point. Writing
  // layerStack also migrates a legacy scene to the entry model.
  const handleMapClick = (lngLat: { lng: number; lat: number }) => {
    if (!inspectionEntry || drawingEntryId) return;
    patchScene(sceneIdx, {
      layerStack: sceneEntries.map((e) =>
        e.id === inspectionEntry.id
          ? { ...e, inspectionPoint: [lngLat.lng, lngLat.lat] }
          : e
      ),
    });
  };

  const compareRef = useRef<CompareMapViewHandle | null>(null);

  const captureCamera = () => {
    const camera =
      sceneMode === "compare"
        ? compareRef.current?.getCamera()
        : canvasRef.current?.getCamera();
    if (camera) patchScene(sceneIdx, { camera });
  };

  // SceneLayerList edits replace the whole entry list; writing layerStack
  // migrates legacy scenes to the entry model on their first layer edit.
  const handleLayerEntriesChange = (
    entries: SceneLayerEntry[],
    opts?: { derived?: boolean }
  ) => patchScene(sceneIdx, { layerStack: entries }, opts);

  // Freehand drawing writes the entry's features on every edit.
  const handleFreehandChange = (features: GeoJSON.FeatureCollection) => {
    if (!drawingEntryId) return;
    patchScene(sceneIdx, {
      layerStack: sceneEntries.map((e) =>
        e.id === drawingEntryId ? { ...e, features } : e
      ),
    });
  };

  const patchLegend = (patch: Partial<SceneLegend>) =>
    patchScene(sceneIdx, {
      legend: { enabled: scene?.legend?.enabled ?? false, ...scene?.legend, ...patch },
    });

  const renderBlockEditor = (block: StoryBlock) => {
    switch (block.type) {
      case "text":
        return (
          <TextBlockEditor block={block} onChange={(p) => patchBlock(block.id, p)} />
        );
      case "heading":
        return (
          <TextField
            size="small"
            fullWidth
            placeholder="Heading"
            value={block.text}
            onChange={(e) => patchBlock(block.id, { text: e.target.value })}
            inputProps={{ style: { fontWeight: 700, fontSize: "1.05rem" } }}
          />
        );
      case "metric":
        return (
          <MetricBlockEditor
            block={block}
            onChange={(p) => patchBlock(block.id, p)}
            scope={scope}
            lists={lists}
          />
        );
      case "metrics_row":
        return (
          <MetricsRowBlockEditor
            block={block}
            onChange={(p) => patchBlock(block.id, p)}
            scope={scope}
            lists={lists}
          />
        );
      case "image":
        return (
          <ImageBlockEditor
            block={block}
            onChange={(p) => patchBlock(block.id, p)}
            scope={scope}
            lists={lists}
            storyMapId={storyMapId}
          />
        );
      default:
        // Unknown block types (e.g. legacy content) are ignored gracefully.
        return null;
    }
  };

  if (!content || !scene) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="text.secondary">Loading story…</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: `calc(var(--vh100) - ${TOPBAR_HEIGHT})`,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* ----- builder header (design 1c) ----- */}
      <Box
        sx={{
          height: HEADER_PX,
          bgcolor: SLATE,
          color: "white",
          display: "flex",
          alignItems: "center",
          px: 1.5,
          gap: 1,
          flexShrink: 0,
        }}
      >
        <IconButton
          sx={{ color: "white" }}
          onClick={() => {
            flushSave();
            // A folder or organisation-wide story came from the listing's
            // story-map browse mode; land back in it.
            if (story && scope.isScoped) browseFlag.set("stories");
            navigate(story ? storyMapPaths.back(story) : "/projects");
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <InputBase
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            setDirty(true);
          }}
          placeholder="Untitled story"
          sx={{
            color: "white",
            fontSize: "1.1rem",
            fontWeight: 600,
            flexGrow: 1,
            maxWidth: 520,
            px: 1,
            borderRadius: 1,
            "&:hover, &.Mui-focused": { bgcolor: "rgba(255,255,255,0.08)" },
          }}
        />
        {saveError ? (
          <Tooltip title={saveError} arrow>
            <Button
              size="small"
              startIcon={<ErrorOutlineIcon fontSize="small" />}
              onClick={save}
              sx={{ color: "#ffb4a2", textTransform: "none", mr: 1 }}
            >
              {saveLabel} — retry
            </Button>
          </Tooltip>
        ) : (
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", mr: 1 }}>
            {saveLabel}
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Reader comments" arrow>
          <IconButton
            onClick={(e) => setCommentsAnchor(e.currentTarget)}
            sx={{ color: commentsAnchor ? TEAL : "rgba(255,255,255,0.85)" }}
          >
            <Badge
              badgeContent={openCommentsTotal}
              max={99}
              sx={{
                "& .MuiBadge-badge": { bgcolor: TEAL, color: SLATE, fontWeight: 700 },
              }}
            >
              <ChatBubbleOutlineIcon />
            </Badge>
          </IconButton>
        </Tooltip>
        <Tooltip title="Show me around" arrow>
          <IconButton
            data-tour={TOUR_ANCHOR.help}
            onClick={tour.start}
            sx={{ color: "rgba(255,255,255,0.85)" }}
          >
            <HelpOutlineIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Story settings" arrow>
          <IconButton
            onClick={() => setStorySettingsOpen(true)}
            sx={{ color: "rgba(255,255,255,0.85)" }}
          >
            <SettingsIcon />
          </IconButton>
        </Tooltip>
        <Button
          variant="outlined"
          startIcon={<VisibilityIcon />}
          onClick={() => {
            flushSave();
            if (story) navigate(storyMapPaths.preview(story));
          }}
          sx={{
            color: "white",
            borderColor: "rgba(255,255,255,0.4)",
            textTransform: "none",
            "&:hover": { borderColor: "white" },
          }}
        >
          Preview
        </Button>
        <Button
          data-tour={TOUR_ANCHOR.publish}
          variant="contained"
          startIcon={<PublishIcon />}
          onClick={() => {
            flushSave();
            setShareOpen(true);
          }}
          sx={{
            bgcolor: TEAL,
            color: SLATE,
            fontWeight: 600,
            textTransform: "none",
            "&:hover": { bgcolor: "#00bd9a" },
          }}
        >
          Publish
        </Button>
      </Box>

      <Box sx={{ display: "flex", flexGrow: 1, minHeight: 0 }}>
        {/* ----- left rail: scene filmstrip ----- */}
        <Box
          data-tour={TOUR_ANCHOR.sceneRail}
          sx={{
            width: 96,
            bgcolor: SLATE,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: 1.5,
            gap: 1,
            overflowY: "auto",
            flexShrink: 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: "rgba(255,255,255,0.45)", letterSpacing: 0.5 }}
          >
            SCENES
          </Typography>
          {scenes.map((s, i) => {
            const openComments = openThreadCount(comments, s.id);
            return (
              <Box
                key={s.id}
                title={
                  openComments > 0
                    ? `${s.name} · ${openComments} open comment${openComments === 1 ? "" : "s"}`
                    : s.name
                }
                onClick={() => setSceneIdx(i)}
                sx={{
                  position: "relative",
                  width: 68,
                  height: 48,
                  borderRadius: 1,
                  border: i === sceneIdx ? `2px solid ${TEAL}` : "2px solid rgba(255,255,255,0.25)",
                  bgcolor: "rgba(255,255,255,0.08)",
                  color: i === sceneIdx ? TEAL : "rgba(255,255,255,0.7)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {i + 1}
                {openComments > 0 && (
                  <Box
                    component="span"
                    sx={{
                      position: "absolute",
                      top: -7,
                      right: -7,
                      minWidth: 18,
                      height: 18,
                      px: 0.5,
                      borderRadius: 9,
                      bgcolor: TEAL,
                      color: SLATE,
                      fontSize: 11,
                      fontWeight: 700,
                      lineHeight: "18px",
                      textAlign: "center",
                    }}
                  >
                    {openComments}
                  </Box>
                )}
              </Box>
            );
          })}
          <Tooltip title="Add scene" arrow>
            <IconButton onClick={addScene} sx={{ color: "rgba(255,255,255,0.8)" }}>
              <AddIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ mt: "auto", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Tooltip title="Move scene up" arrow>
              <span>
                <IconButton
                  size="small"
                  disabled={sceneIdx === 0}
                  onClick={() => moveScene(sceneIdx, -1)}
                  sx={{ color: "rgba(255,255,255,0.8)" }}
                >
                  <KeyboardArrowUpIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Move scene down" arrow>
              <span>
                <IconButton
                  size="small"
                  disabled={sceneIdx >= scenes.length - 1}
                  onClick={() => moveScene(sceneIdx, 1)}
                  sx={{ color: "rgba(255,255,255,0.8)" }}
                >
                  <KeyboardArrowDownIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete scene" arrow>
              <span>
                <IconButton
                  size="small"
                  disabled={scenes.length <= 1}
                  onClick={() => deleteScene(sceneIdx)}
                  sx={{ color: "rgba(255,255,255,0.8)" }}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* ----- center: scene editor ----- */}
        <Box
          data-tour={TOUR_ANCHOR.sceneEditor}
          sx={{
            width: 440,
            borderRight: "1px solid",
            borderColor: "divider",
            overflowY: "auto",
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            flexShrink: 0,
            bgcolor: "#fafbfc",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, mb: -0.5 }}>
            <Typography variant="subtitle2">
              Scene {sceneIdx + 1} narrative
            </Typography>
            <HelpTip title="The narrative column">
              <p>
                This is the text the reader sees beside the map while scene{" "}
                {sceneIdx + 1} is on screen.
              </p>
              <p>
                The <strong>kicker</strong> is a small label above the heading —
                a section number or theme. The <strong>scene name</strong> is
                for you; readers never see it.
              </p>
            </HelpTip>
          </Box>

          <TextField
            label="Kicker"
            placeholder={`0${sceneIdx + 1} · Section label`}
            size="small"
            value={scene.kicker ?? ""}
            onChange={(e) => patchScene(sceneIdx, { kicker: e.target.value })}
          />
          <TextField
            label="Scene name"
            size="small"
            value={scene.name}
            onChange={(e) => patchScene(sceneIdx, { name: e.target.value })}
          />

          {scene.blocks.length === 0 && (
            <Paper
              variant="outlined"
              sx={{ p: 2, bgcolor: "rgba(0,212,173,0.06)", borderColor: TEAL }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Nothing to read yet
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                A scene with no blocks shows the map but says nothing. Add a
                heading and a paragraph below to tell the reader what they're
                looking at.
              </Typography>
            </Paper>
          )}

          {scene.blocks.map((block, i) => (
            <BlockShell
              key={block.id}
              label={BLOCK_LABELS[block.type]}
              canMoveUp={i > 0}
              canMoveDown={i < scene.blocks.length - 1}
              onMoveUp={() => patchSceneBlocks((b) => moveItem(b, i, i - 1))}
              onMoveDown={() => patchSceneBlocks((b) => moveItem(b, i, i + 1))}
              onDuplicate={() => duplicateBlock(block.id)}
              onDelete={() => patchSceneBlocks((b) => b.filter((x) => x.id !== block.id))}
            >
              {renderBlockEditor(block)}
            </BlockShell>
          ))}

          {/* Add-a-block palette */}
          <Paper variant="outlined" sx={{ p: 1.5, borderStyle: "dashed" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Add a block
              </Typography>
              <HelpTip title="Blocks">
                <p>
                  Blocks stack down the narrative column in the order you add
                  them.
                </p>
                <p>
                  <strong>Text</strong> and <strong>Heading</strong> are prose.{" "}
                  <strong>Metric</strong> and <strong>Metrics row</strong> are
                  figures with a label and units. <strong>Image</strong> takes a
                  project snapshot or an upload.
                </p>
              </HelpTip>
            </Box>
            <Box
              sx={{
                mt: 1,
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 1,
              }}
            >
              {BLOCK_PALETTE.map((entry) => (
                <Button
                  key={entry.type}
                  size="small"
                  startIcon={entry.icon}
                  onClick={() => addBlock(entry.type)}
                  sx={{
                    justifyContent: "flex-start",
                    textTransform: "none",
                    color: "text.secondary",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  {entry.label}
                </Button>
              ))}
            </Box>
          </Paper>
        </Box>

        {/* ----- map panel + scene status strip ----- */}
        <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
          <Box
            data-tour={TOUR_ANCHOR.map}
            sx={{ flexGrow: 1, minHeight: 0, position: "relative" }}
          >
            {sceneMode === "compare" && scene.compare ? (
              <CompareMapView
                ref={compareRef}
                compare={scene.compare}
                camera={scene.camera}
                isPublic={false}
                sources={sources}
                fitTo={boundaryFeature}
                legend={scene.legend}
              />
            ) : (
              <>
                <StoryMapCanvas
                  ref={canvasRef}
                  layers={mapLayers}
                  fitTo={boundaryFeature}
                  initialBounds={boundaryFeature}
                  camera={scene.camera}
                  transition={scene.transition}
                  interactive
                  onLayerClick={handleLayerClick}
                  onMapClick={handleMapClick}
                  onMapReady={setMapInstance}
                />
                {weatherPanel && (
                  <WeatherPanelCard
                    data={weatherPanel}
                    side={weatherPanel.side}
                    unitSystem={unitSystem}
                  />
                )}
                <WindParticleLayer
                  map={mapInstance}
                  isActive={!!wind && !!mapInstance}
                  windSpeed={wind?.windSpeed ?? 0}
                  windDirection={wind?.windDirection ?? 0}
                  field={wind?.field ?? null}
                  bounds={windBounds ?? { top: 0, bottom: 0, left: 0, right: 0 }}
                />
                <StoryFuel3DLayers map={mapInstance} items={fuel3d} crownsEnabled />
                {sceneMode === "animation" && selectedIgnition && (
                  <Chip
                    size="small"
                    icon={<WhatshotIcon sx={{ color: `${TEAL} !important` }} />}
                    label={`Ignition ${selectedIgnition.label}`}
                    sx={{
                      position: "absolute",
                      top: 12,
                      left: 12,
                      zIndex: 3,
                      bgcolor: "rgba(33,43,55,0.85)",
                      color: "white",
                      pointerEvents: "none",
                    }}
                  />
                )}
                {sceneMode === "animation" && anim.overlay}
                {sceneMode === "animation" && anim.controls}
                {sceneMode === "animation" && anim.legend}
                {scene.legend?.enabled && (
                  <StoryLegend rows={legendRows} legend={scene.legend} />
                )}
                {drawingEntry && (
                  <FreehandDrawTool
                    key={drawingEntry.id}
                    getMap={() => canvasRef.current?.getMap() ?? null}
                    features={drawingEntry.features}
                    onChange={handleFreehandChange}
                    onDone={() => setDrawingEntryId(null)}
                  />
                )}
              </>
            )}
          </Box>
          <Box
            sx={{
              height: 40,
              bgcolor: SLATE,
              color: "white",
              display: "flex",
              alignItems: "center",
              px: 1,
              flexShrink: 0,
            }}
          >
            <IconButton
              size="small"
              sx={{ color: "white" }}
              disabled={sceneIdx === 0}
              onClick={() => setSceneIdx((i) => i - 1)}
            >
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="body2" sx={{ mx: 1 }}>
              Scene {sceneIdx + 1} of {scenes.length} · {scene.name}
            </Typography>
            <IconButton
              size="small"
              sx={{ color: "white" }}
              disabled={sceneIdx >= scenes.length - 1}
              onClick={() => setSceneIdx((i) => i + 1)}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>

        {/* ----- right rail: scene settings ----- */}
        <Box
          sx={{
            width: 300,
            borderLeft: "1px solid",
            borderColor: "divider",
            overflowY: "auto",
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TuneIcon fontSize="small" sx={{ color: "text.secondary" }} />
            <Typography variant="subtitle2">Scene settings</Typography>
          </Box>

          <Box data-tour={TOUR_ANCHOR.mapMode}>
            <HelpLabel label="Map mode" title="Map mode">
              <p>
                Decides what the map <em>is</em> for this scene. The settings
                below change to match.
              </p>
              <p>
                <strong>Layers</strong> —{" "}
                {scope.isScoped
                  ? "layers from any project the story draws on (each layer names its project): boundary, treatments, assets, fuel, vegetation, fire history, at a chosen strategy and date."
                  : "the project's own layers (boundary, treatments, assets, fuel, vegetation, fire history) at a chosen strategy and date."}
              </p>
              <p>
                <strong>Compare</strong> — two landscape states side by side,
                split by a handle the reader drags.
              </p>
              <p>
                <strong>Animation</strong> — one or more completed fire
                simulations playing over the map on a shared clock.
              </p>
            </HelpLabel>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={sceneMode}
              onChange={(_, value: SceneMode | null) => {
                if (value) setSceneMode(value);
              }}
              sx={{ mt: 0.5 }}
            >
              <ToggleButton value="layers" sx={{ textTransform: "none", px: 0.5 }}>
                <Tooltip title="Map layers" arrow>
                  <LayersIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="compare" sx={{ textTransform: "none", px: 0.5 }}>
                <Tooltip title="Before / after compare" arrow>
                  <CompareIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="animation" sx={{ textTransform: "none", px: 0.5 }}>
                <Tooltip title="Simulation animation" arrow>
                  <MovieIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box>
            <HelpLabel label="On scroll" title="On scroll">
              <p>
                How the map gets from the previous scene's view to this one as
                the reader arrives.
              </p>
              <p>
                <strong>Fly to camera</strong> animates across the landscape —
                good for showing the relationship between two places.{" "}
                <strong>Jump</strong> cuts straight there — better for long
                distances, where a fly-over is just a wait.
              </p>
            </HelpLabel>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={scene.transition}
              onChange={(_, value: StoryTransition | null) => {
                if (value) patchScene(sceneIdx, { transition: value });
              }}
              sx={{ mt: 0.5 }}
            >
              <ToggleButton value="fly" sx={{ textTransform: "none" }}>
                Fly to camera
              </ToggleButton>
              <ToggleButton value="jump" sx={{ textTransform: "none" }}>
                Jump
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box data-tour={TOUR_ANCHOR.camera}>
            <HelpLabel label="Camera" title="Camera">
              <p>
                The view the map moves to when the reader reaches this scene —
                centre, zoom, tilt and rotation, saved on this scene alone.
              </p>
              <p>
                Pan and zoom the map to where you want them to land, then press{" "}
                <strong>Use current view</strong>.
              </p>
            </HelpLabel>
            {scene.camera ? (
              <Typography
                variant="body2"
                sx={{ fontFamily: "monospace", fontSize: 12, my: 0.5 }}
              >
                {`${scene.camera.center[1].toFixed(4)}, ${scene.camera.center[0].toFixed(4)} · z${scene.camera.zoom} · pitch ${scene.camera.pitch}° · brg ${scene.camera.bearing}°`}
              </Typography>
            ) : (
              // Not an error — but a scene with no camera silently inherits
              // whatever view the reader was already on, which is rarely what
              // the author meant.
              <Typography
                variant="caption"
                sx={{ color: "warning.main", display: "block", my: 0.5 }}
              >
                Not set — this scene won't move the map. Frame it below.
              </Typography>
            )}
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              startIcon={<PhotoCameraIcon />}
              onClick={captureCamera}
              sx={{ textTransform: "none" }}
            >
              Use current view
            </Button>
          </Box>

          {sceneMode === "compare" && scene.compare && (
            <CompareSceneSettings
              compare={scene.compare}
              onChange={patchCompare}
              scope={scope}
              lists={lists}
            />
          )}

          {sceneMode === "animation" && (
            <AnimationSceneSettings
              animations={animations}
              selectedId={selectedAnimEntryId}
              onSelect={setSelectedAnimId}
              onEntryChange={patchAnimationEntry}
              onAdd={addAnimationEntry}
              onRemove={removeAnimationEntry}
              onMove={moveAnimationEntry}
              scope={scope}
              lists={lists}
            />
          )}

          {sceneMode !== "compare" && (
          <Box>
            <HelpLabel label="Layers in scene" title="Layers in scene">
              <p>
                Which layers draw on the map for this scene. Each scene chooses
                its own — that's how a story moves from context to detail
                without the reader touching anything.
                {sceneMode === "animation" &&
                  " In an animation scene they draw under the fire, so the reader sees what it burns through."}
              </p>
              <p>
                <strong>Add layer</strong> asks for that layer's settings —
                treatments pick a strategy plus type/date filters, fuel and
                vegetation a strategy and date, the risk layers a simulation.
                Added layers appear as chips: click one to edit it, or its ×
                to remove it.
              </p>
            </HelpLabel>
            <SceneLayerList
              entries={sceneEntries}
              onChange={handleLayerEntriesChange}
              scope={scope}
              lists={lists}
              onDrawEntry={
                sceneMode === "layers" ? (entry) => setDrawingEntryId(entry.id) : undefined
              }
            />
          </Box>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    color="secondary"
                    checked={!!scene.legend?.enabled}
                    onChange={(e) => patchLegend({ enabled: e.target.checked })}
                  />
                }
                label={<Typography variant="body2">Show legend</Typography>}
                sx={{ mr: 0.25 }}
              />
              <HelpTip title="Legend">
                <p>
                  A card on the map listing the scene's layers with their
                  colours and styles — built from the layers above, so it
                  always matches what is drawn.
                </p>
                <p>
                  Rename or hide individual rows from each layer's settings
                  (click its chip). Compare scenes list both sides' layers;
                  animation scenes list the layers drawn under the fire.
                </p>
              </HelpTip>
            </Box>
            {scene.legend?.enabled && (
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  size="small"
                  label="Title"
                  placeholder="Legend"
                  value={scene.legend?.title ?? ""}
                  onChange={(e) => patchLegend({ title: e.target.value || undefined })}
                  sx={{ flex: 1 }}
                />
                <TextField
                  select
                  size="small"
                  label="Position"
                  value={scene.legend?.position ?? "bottom-right"}
                  onChange={(e) => patchLegend({ position: e.target.value as LegendPosition })}
                  sx={{ minWidth: 130 }}
                >
                  <MenuItem value="top-left">Top left</MenuItem>
                  <MenuItem value="top-right">Top right</MenuItem>
                  <MenuItem value="bottom-left">Bottom left</MenuItem>
                  <MenuItem value="bottom-right">Bottom right</MenuItem>
                </TextField>
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  color="secondary"
                  checked={scene.explorable}
                  onChange={(e) => patchScene(sceneIdx, { explorable: e.target.checked })}
                />
              }
              label={
                <Typography variant="body2">
                  Reader can pan &amp; zoom this scene
                </Typography>
              }
              sx={{ mr: 0.25 }}
            />
            <HelpTip title="Reader can pan &amp; zoom">
              <p>
                Off by default: the map is yours to frame, and the reader just
                scrolls.
              </p>
              <p>
                Turn it on for a scene you want them to explore — but they can
                then wander off your framing, and scrolling on the map zooms
                rather than advancing the story.
              </p>
            </HelpTip>
          </Box>

          <Typography variant="caption" sx={{ color: "text.secondary", mt: "auto" }}>
            Scene data freezes when the story is published.
          </Typography>
        </Box>
      </Box>

      {/* Reader comments: a panel hanging off the top bar over the map's
          top-right corner, showing the selected scene's threads. Stays open
          while the author moves between scenes. */}
      <Popover
        open={!!commentsAnchor}
        anchorEl={commentsAnchor}
        onClose={() => setCommentsAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        marginThreshold={8}
        slotProps={{
          paper: {
            sx: {
              width: 400,
              maxWidth: "calc(100vw - 16px)",
              maxHeight: "calc(100vh - 120px)",
              display: "flex",
              flexDirection: "column",
              borderRadius: 2,
              mt: 0.5,
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 1,
            bgcolor: SLATE,
            color: "white",
            flexShrink: 0,
          }}
        >
          <IconButton
            size="small"
            sx={{ color: "white" }}
            disabled={sceneIdx === 0}
            onClick={() => setSceneIdx((i) => i - 1)}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="body2" sx={{ flexGrow: 1, textAlign: "center" }} noWrap>
            Scene {sceneIdx + 1} of {scenes.length} · {scene.name}
          </Typography>
          <IconButton
            size="small"
            sx={{ color: "white" }}
            disabled={sceneIdx >= scenes.length - 1}
            onClick={() => setSceneIdx((i) => i + 1)}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
        <Box sx={{ p: 2, overflowY: "auto" }}>
          {!storyData?.data.comments_enabled && (
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1.5 }}>
              Readers can't comment yet — switch on comments in Publish. Any
              comments left while it was on are still here.
            </Typography>
          )}
          <SceneComments
            variant="builder"
            sceneId={scene.id}
            comments={comments}
            viewerName={user?.username ?? "You"}
            isOrgMember
            busy={commentsBusy}
            error={commentsError}
            onCreate={(input) => commentActions.create.mutateAsync(input)}
            onResolve={(id, resolved) =>
              commentActions.resolve.mutateAsync({ id, resolved })
            }
            onDelete={(id) => commentActions.remove.mutateAsync(id)}
          />
        </Box>
      </Popover>

      {/* Story-level settings (subtitle/author) — same autosave path as the
          scene rail (editContent marks dirty → debounced PATCH). */}
      <Dialog
        open={storySettingsOpen}
        onClose={() => setStorySettingsOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Story settings</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}
        >
          <TextField
            label="Subtitle"
            size="small"
            multiline
            minRows={2}
            value={content.subtitle ?? ""}
            onChange={(e) => editContent((c) => ({ ...c, subtitle: e.target.value }))}
          />
          <TextField
            label="Author name"
            size="small"
            value={content.authorName ?? ""}
            onChange={(e) => editContent((c) => ({ ...c, authorName: e.target.value }))}
          />
          <TextField
            label="Author role"
            size="small"
            value={content.authorRole ?? ""}
            onChange={(e) => editContent((c) => ({ ...c, authorRole: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setStorySettingsOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {storyData?.data && (
        <ShareDialog
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          story={{
            id: storyData.data.id,
            title,
            status: storyData.data.status,
            visibility: storyData.data.visibility,
            slug: storyData.data.slug,
            comments_enabled: storyData.data.comments_enabled,
          }}
          scopeLabel={scopeLabel}
        />
      )}
    </Box>
  );
}
